'use client';

import React, { useState, useEffect } from 'react';

interface Centre {
  nom: string;
  commune: string;
  telephone: string;
}

const PhoneIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/**
 * Bouton « Appeler un centre » : ouvre une modale listant les centres,
 * avec un lien d'appel (tel:) par centre. Remplace l'ancien numéro unique en dur.
 */
export default function AppelerCentres({ centres }: { centres: Centre[] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!centres.length) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="btn-outline-white text-sm"
      >
        <PhoneIcon />
        Appeler un centre
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Choisir un centre à appeler"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="font-bold text-text-main text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>Appeler un centre</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                className="w-8 h-8 rounded-full flex items-center justify-center text-text-muted hover:bg-gray-100 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto p-2">
              {centres.map((c) => (
                <a
                  key={c.nom}
                  href={`tel:${c.telephone.replace(/\s/g, '')}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl hover:bg-primary/5 transition-colors"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-text-main truncate" style={{ fontFamily: 'Poppins, sans-serif' }}>{c.nom}</span>
                    {c.commune && <span className="block text-xs text-text-muted" style={{ fontFamily: 'Poppins, sans-serif' }}>{c.commune}</span>}
                  </span>
                  <span className="flex items-center gap-1.5 text-primary text-sm font-medium whitespace-nowrap" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <PhoneIcon size={15} />
                    {c.telephone}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
